import { EPaymentFrequency, EPaymentMethod } from '@if/core/types'
import { getEnvRuntimeVariable, Getter, lazyLoad } from '@if/core/utils'
import { useTranslatedOptionEnum } from '@if/mui'
import { MenuItem } from '@mui/material'
import { createElement } from 'react'
import { useTranslation } from 'react-i18next'

export const SYSTEM_CONSTANT_CHECK_FOR_STALE_STATE_INTERVAL = 30 * 1000
export const USER_CREATED_PAYMENT_CLASS = 'user-created-payment'

export enum ETerm {
  Twelve = 12,
  TwentyFour = 24,
  ThirtySix = 36,
  FortyEight = 48,
  Sixty = 60,
  SeventyTwo = 72,
  EightyFour = 84,
}

export enum ELoanStatus {
  Active = 'active',
  Pending = 'pending',
  Bankcruptcy = 'bankcruptcy',
  NotRequired = 'notRequired',
  LegalProcess = 'legalProcess',
  Provision = 'provision',
  StruckOff = 'struckOff',
  EarlyRepayment = 'earlyRepayment',
  FullRefund = 'fullRefund',
  VoluntarySurrender = 'voluntarySurrender',
  VehicleRecovery = 'vehicleRecovery',
  Claim = 'claim',
  ClosedClaim = 'closedClaim',
  WageGarnishment = 'wageGarnishment',
  TemporarilySuspended = 'temporarilySuspended',
  Finished = 'finished',
  TransfertInProgress = 'transfertInProgress',
}

export enum EProduct {
  NsfFee = 1,
  AdminFee = 2,
  FinanceFee = 3,
  CapitalFinanced = 4,
  IFinanceInsurance = 5,
  Interest = 6,
}

export enum EPayoutTarget {
  Merchant = 'merch',
  Borrower = 'borr',
}

export enum EFeeBillingMechanism {
  ChargedOnPayout = 'payout',
  ChargedAfterPromo = 'promo',
  ChargedOnAnniversary = 'yearly',
}

export enum EInterestRoundingMethod {
  Round = 1,
  Trunk = 2,
}

export enum EInterestComputeMethod {
  OutstandingPrincipal = 'outstanding',
  UnbilledPrincipal = 'unbilled',
}

export enum EOperationStatus {
  Planned = 'planned',
  Cancelled = 'cancelled',
  Complete = 'complete',
  Failed = 'reverted',
}

export enum ECatchUpLatePaymentMethod {
  CatchUpOnSinglePayment = 'catchUpOnSinglePayment',
  CatchUpOnRemainingPayments = 'catchUpOnRemainingPayments',
  CatchUpOnNextPayments = 'catchUpOnNextPayments',
}

export const EXECUTED_OPERATION_STATUSES = [
  EOperationStatus.Complete,
  EOperationStatus.Failed,
]

export enum EBankFailureCode {
  REVAdministrator = 555,
  RejectedFollowingEditing = 900,
  NSFInsufficientFunds = 901,
  AccountNotFound = 902,
  PaymentOppositionRevocation = 903,
  PostdatedExpiredCheque = 904,
  AccountClosed = 905,
  AccountTransferred = 906,
  NoChequePrivilege = 907,
  UnclearedFunds = 908,
  Deceased = 910,
  AccountFrozen = 911,
  IncorrectAccountNumber = 912,
  NameError = 914,
  RefusedByDebtor = 915,
  NonCompliantWithAgreementPersonal = 916,
  AgreementRevokedPersonal = 917,
  NoPriorNotificationPersonal = 918,
  NonCompliantWithAgreementBusiness = 919,
  AgreementRevokedBusiness = 920,
  NoPriorNotificationBusiness = 921,
  CreditReturnInitiatedByClient = 922,
  DefaultingInstitution = 990,
}

export enum EMetro2OverrideReasons {
  IcebergError = 'icebergError',
  ClientChance = 'clientChance',
}

export const getELoanStatusList: Getter<ELoanStatus[]> = lazyLoad(() => Object.values(ELoanStatus))
export const getEProductList: Getter<EProduct[]> = lazyLoad(() =>
  Object.values(EProduct).filter((x) => typeof x === 'number'),
)
export const getEPayoutTargetList: Getter<EPayoutTarget[]> = lazyLoad(() => Object.values(EPayoutTarget))
export const getETermList: Getter<ETerm[]> = lazyLoad(() => Object.values(ETerm).filter((x) => typeof x === 'number'))
export const SUPPORTED_DOCUMENT_TYPES = '.jpg, .jpeg, .png, .pdf, .heic, .heif'
export const SYSTEM_USER_ID = 'system'
export const CLEAR_CACHE_EXCEPTION_KEY = ['mui-mode']
export const getEFeeBillingMechanismList: Getter<EFeeBillingMechanism[]> = lazyLoad(() =>
  Object.values(EFeeBillingMechanism),
)
export const getEInterestRoundingMethodList: Getter<EInterestRoundingMethod[]> = lazyLoad(() =>
  Object.values(EInterestRoundingMethod).filter((x) => typeof x === 'number'),
)
export const getEInterestComputeMethodList: Getter<EInterestComputeMethod[]> = lazyLoad(() =>
  Object.values(EInterestComputeMethod),
)
export const SUPPORTED_FREQUENCIES = [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly]
export const useTranslatedPaymentFrequency = () => {
  return useTranslatedOptionEnum('EPaymentFrequency', SUPPORTED_FREQUENCIES, 'enum')
}
export const VALID_PAYMENT_METHODS = [
  EPaymentMethod.PreAuthorizedPayment,
  EPaymentMethod.Check,
  EPaymentMethod.Cash,
  EPaymentMethod.InteracETransfer,
]
export const useTranslatedPaymentMethods = () => {
  return useTranslatedOptionEnum('EPaymentMethod', VALID_PAYMENT_METHODS, 'enum')
}
export const VALID_CATCHUP_LATE_PAYMENT_METHODS = [
  ECatchUpLatePaymentMethod.CatchUpOnSinglePayment,
  ECatchUpLatePaymentMethod.CatchUpOnRemainingPayments,
  ECatchUpLatePaymentMethod.CatchUpOnNextPayments,
]

export const useTranslatedCatchUpLatePaymentMethods = () => {
  const { t } = useTranslation()
  return VALID_CATCHUP_LATE_PAYMENT_METHODS.map((method) =>
    createElement(MenuItem, { key: method, value: method }, t(`enum.ECatchUpLatePaymentMethod.${method}`)),
  )
}

export const getEBankFailureCodeList: Getter<EBankFailureCode[]> = lazyLoad(() =>
  Object.values(EBankFailureCode).filter((x) => typeof x === 'number'),
)

export const useTranslatedBankFailureCode = () => {
  return useTranslatedOptionEnum('enum.bankFailureCode', getEBankFailureCodeList())
}

export const getEOperationStatusList: Getter<EOperationStatus[]> = lazyLoad(() => Object.values(EOperationStatus))

export const falsy = () => false

// To disable it just set the env var to an empty string
export const useDevTools = getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEV_TOOLS)

export const DEFAULT_PAYMENT_METHOD = EPaymentMethod.PreAuthorizedPayment

export enum EMetro2AccountStatus {
  Current = 0,
  ThirtyDays = 1,
  SixtyDays = 2,
  NinetyDays = 3,
  OneTwentyDays = 4,
  OneFiftyDays = 5,
  OneEightyDays = 6,
}

export enum EFeeType {
  GeneralFees = 'generalFees',
  NsfFees = 'nsfFees',
}

const PRODUCT_TO_FEE_TYPE: Record<number, EFeeType> = {
  [EProduct.AdminFee]: EFeeType.GeneralFees,
  [EProduct.NsfFee]: EFeeType.NsfFees,
}

export const getFeeTypeFromProduct = (productId: EProduct): EFeeType | null => {
  return PRODUCT_TO_FEE_TYPE[productId] ?? null
}

export const VALID_FEE_TYPES = [EFeeType.GeneralFees, EFeeType.NsfFees]

export const useTranslatedFeeTypeList = () => {
  return useTranslatedOptionEnum('enum.EFeeType', VALID_FEE_TYPES)
}